import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Button,
  Select,
  FormControl,
  InputLabel,
  MenuItem,
  TextField,
  OutlinedInput,
  FormHelperText,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  Chip,
  Autocomplete,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate, useParams } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { useTheme } from "@mui/material/styles";
import { connect } from "react-redux";
import { PageHeader, Card, ActionButton } from "../shared";
import ApiServices from "../../config/ApiServices";
import ApiEndpoint from "../../config/ApiEndpoint";
import Constants from "../../config/Constants";

const PLACEMENT_OPTIONS = [
  { value: "global", label: "Dashboard" },
  { value: "footer", label: "Global Footer" },
];

const ORG_TYPE_OPTIONS = [
  { value: "",     label: "All Orgs" },
  { value: "paid", label: "Paid-Org" },
  { value: "sub",  label: "Club" },
];

const PLACEMENT_GUIDE = [
  { zone: "Dashboard",     size: "600 × 800 px (4:5)", where: "Dashboard card slot, object-fit: cover" },
  { zone: "Global Footer", size: "728 × 160 px", where: "Above footer bar" },
];

const Edit_Advertisement = (props) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const navigate = useNavigate();
  const { id } = useParams();
  const token = props?.profile?.token;

  const [imageId, setImageId] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(true);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [orgs, setOrgs] = useState([]);
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  const [targetSpecificOrgs, setTargetSpecificOrgs] = useState(false);
  const [selectedOrgIds, setSelectedOrgIds] = useState([]);
  const orgIdsRef = useRef([]);

  useEffect(() => {
    fetchDetails();
    fetchOrgs();
  }, []);

  const fetchOrgs = async () => {
    setLoadingOrgs(true);
    try {
      const res = await ApiServices.PostApiCall(
        ApiEndpoint.ADVERTISEMENT_ORGS,
        JSON.stringify({}),
        { "Content-Type": "application/json", "x-access-token": token }
      );
      if (res?.status) {
        const all = (res.data || []).map((o) => ({
          id: String(o.id),
          label: o.name,
        }));
        setOrgs(all);
      }
    } catch {
      toast.error("Could not load clubs. Club targeting may be unavailable.");
    } finally {
      setLoadingOrgs(false);
    }
  };

  const fetchDetails = async () => {
    setLoadingDetails(true);
    try {
      const res = await ApiServices.PostApiCall(
        ApiEndpoint.ADVERTISEMENT_VIEW,
        JSON.stringify({ id }),
        { "Content-Type": "application/json", "x-access-token": token }
      );
      if (res?.status && res.data) {
        const d = res.data;
        setImageId(d.id_item_logo);
        setImageUrl(d.logo_url);
        const loadedOrgIds = (d.org_ids || []).map(String);
        orgIdsRef.current = loadedOrgIds;
        setSelectedOrgIds(loadedOrgIds);
        setTargetSpecificOrgs(loadedOrgIds.length > 0);
        formik.setValues({
          websiteLink: d.url_link || "",
          placement: d.placement || "",
          displayOrder: d.display_order ?? 0,
          rotationDuration: d.rotation_duration ?? 5,
          startDate: d.start_date ? d.start_date.slice(0, 10) : "",
          endDate: d.end_date ? d.end_date.slice(0, 10) : "",
          orgType: d.org_type || "",
        });
      } else {
        toast.error("Failed to load advertisement details.");
      }
    } catch {
      toast.error("Failed to load advertisement details.");
    } finally {
      setLoadingDetails(false);
    }
  };

  const uploadImage = async (file) => {
    const formData = new FormData();
    const headers = new Headers();
    headers.append("x-access-token", token);
    formData.append("file", file, file.name);
    formData.append("type", "image");
    setUploading(true);
    try {
      const res = await fetch(`${Constants.BASE_API_URL}user/upload`, {
        method: "POST",
        headers,
        body: formData,
      });
      return await res.json();
    } catch {
      return null;
    } finally {
      setUploading(false);
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== "image/jpeg" && file.type !== "image/png") {
      toast.error("Only JPG and PNG files are allowed.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size must be less than 5MB.");
      return;
    }
    const result = await uploadImage(file);
    if (result?.status && result?.data?.id) {
      setImageId(result.data.id);
      setImageUrl(result.data.url || URL.createObjectURL(file));
      toast.success("Image uploaded");
    } else {
      toast.error("Failed to upload image.");
    }
  };

  const validationSchema = Yup.object({
    websiteLink: Yup.string().url("Enter a valid URL").required("Website Link is required"),
    placement: Yup.string().required("Select a placement"),
    displayOrder: Yup.number().min(0, "Must be 0 or greater"),
    rotationDuration: Yup.number().min(1, "Must be at least 1 second"),
  });

  const formik = useFormik({
    initialValues: {
      websiteLink: "",
      placement: "",
      displayOrder: 0,
      rotationDuration: 5,
      startDate: "",
      endDate: "",
      orgType: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      if (!imageId) {
        toast.error("Please upload an image first.");
        return;
      }
      setSubmitting(true);
      try {
        const body = {
          id,
          url_link: values.websiteLink,
          id_item_logo: imageId.toString(),
          placement: values.placement,
          display_order: parseInt(values.displayOrder),
          rotation_duration: parseInt(values.rotationDuration),
          ...(values.startDate ? { start_date: values.startDate } : {}),
          ...(values.endDate ? { end_date: values.endDate } : {}),
          org_type: values.orgType || null,
          org_ids: orgIdsRef.current.map(Number),
        };
        const res = await ApiServices.PostApiCall(
          ApiEndpoint.EDIT_ADVERTISEMENT,
          JSON.stringify(body),
          { "Content-Type": "application/json", "x-access-token": token }
        );
        if (res?.status) {
          toast.success(res.message || "Advertisement updated", {
            onClose: () => navigate("/advertisement"),
            autoClose: 2000,
          });
        } else {
          toast.error(res?.message || "Failed to update advertisement");
        }
      } catch {
        toast.error("Something went wrong. Please try again.");
      } finally {
        setSubmitting(false);
      }
    },
  });

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: isDark ? "rgba(255,255,255,0.04)" : "#f7fafc",
      borderRadius: "8px",
    },
  };

  if (loadingDetails) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 300 }}>
        <CircularProgress color={isDark ? "secondary" : "primary"} />
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Edit Advertisement"
        subtitle="Update the details of this advertisement"
        breadcrumbs={[
          { label: "Admin" },
          { label: "Advertisements", href: "/advertisement" },
          { label: "Edit" },
        ]}
      >
        <ActionButton
          variant="secondary"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/advertisement")}
        >
          Back
        </ActionButton>
      </PageHeader>

      <form onSubmit={formik.handleSubmit}>
        <Grid container spacing={3}>
          {/* Main form */}
          <Grid item xs={12} md={8}>
            <Card title="Advertisement Details">
              <Grid container spacing={2}>
                {/* Placement */}
                <Grid item xs={12}>
                  <FormControl
                    fullWidth
                    error={formik.touched.placement && Boolean(formik.errors.placement)}
                    sx={inputSx}
                  >
                    <InputLabel>Placement Zone *</InputLabel>
                    <Select
                      name="placement"
                      value={formik.values.placement}
                      onChange={formik.handleChange}
                      input={<OutlinedInput label="Placement Zone *" />}
                    >
                      {PLACEMENT_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                    {formik.touched.placement && formik.errors.placement && (
                      <FormHelperText>{formik.errors.placement}</FormHelperText>
                    )}
                    {formik.values.placement && !formik.errors.placement && (
                      <FormHelperText sx={{ color: isDark ? "#a78bfa" : "#1976d2", fontWeight: 600 }}>
                        Required size: {PLACEMENT_GUIDE.find((g) => g.zone === PLACEMENT_OPTIONS.find((o) => o.value === formik.values.placement)?.label)?.size}
                      </FormHelperText>
                    )}
                  </FormControl>
                </Grid>

                {/* Website Link */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Website Link *"
                    name="websiteLink"
                    value={formik.values.websiteLink}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.websiteLink && Boolean(formik.errors.websiteLink)}
                    helperText={formik.touched.websiteLink && formik.errors.websiteLink}
                    placeholder="https://example.com"
                    sx={inputSx}
                  />
                </Grid>

                {/* Org Type */}
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth sx={inputSx}>
                    <InputLabel>Org Type (optional)</InputLabel>
                    <Select
                      name="orgType"
                      value={formik.values.orgType}
                      onChange={formik.handleChange}
                      input={<OutlinedInput label="Org Type (optional)" />}
                    >
                      {ORG_TYPE_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>Leave blank to target all orgs</FormHelperText>
                  </FormControl>
                </Grid>

                {/* Target Specific Orgs toggle */}
                <Grid item xs={12} sm={6} sx={{ display: "flex", alignItems: "center" }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={targetSpecificOrgs}
                        onChange={(e) => {
                          setTargetSpecificOrgs(e.target.checked);
                          if (!e.target.checked) {
                            orgIdsRef.current = [];
                            setSelectedOrgIds([]);
                          }
                        }}
                        sx={{ color: isDark ? "#818cf8" : "#1976d2" }}
                      />
                    }
                    label={
                      <Typography sx={{ fontSize: "0.875rem", color: isDark ? "#e2e8f0" : "#334155" }}>
                        Target Specific Orgs
                      </Typography>
                    }
                  />
                </Grid>

                {/* Org IDs multi-select */}
                {targetSpecificOrgs && (
                  <Grid item xs={12}>
                    <Autocomplete
                      multiple
                      options={orgs}
                      loading={loadingOrgs}
                      getOptionLabel={(option) => option.label}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      value={orgs.filter((o) => selectedOrgIds.includes(o.id))}
                      onChange={(_, newValue) => {
                        const ids = newValue.map((o) => o.id);
                        orgIdsRef.current = ids;
                        setSelectedOrgIds(ids);
                      }}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            label={option.label}
                            size="small"
                            {...getTagProps({ index })}
                            sx={{
                              backgroundColor: isDark ? "rgba(99,102,241,0.2)" : "rgba(25,118,210,0.08)",
                              color: isDark ? "#818cf8" : "#1976d2",
                            }}
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Target Specific Orgs"
                          placeholder={loadingOrgs ? "Loading orgs..." : "Search by org name..."}
                          sx={inputSx}
                        />
                      )}
                    />
                    <FormHelperText sx={{ mx: "14px" }}>
                      If selected, specific orgs take precedence over Org Type
                    </FormHelperText>
                  </Grid>
                )}
              </Grid>
            </Card>

            {/* Rotation Settings */}
            <Box sx={{ mt: 3 }}>
              <Card title="Rotation Settings">
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Display Order"
                      name="displayOrder"
                      type="number"
                      value={formik.values.displayOrder}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.displayOrder && Boolean(formik.errors.displayOrder)}
                      helperText={(formik.touched.displayOrder && formik.errors.displayOrder) || "0 = shown first"}
                      inputProps={{ min: 0 }}
                      sx={inputSx}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Rotation Duration (seconds)"
                      name="rotationDuration"
                      type="number"
                      value={formik.values.rotationDuration}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.rotationDuration && Boolean(formik.errors.rotationDuration)}
                      helperText={(formik.touched.rotationDuration && formik.errors.rotationDuration) || "How long this ad shows"}
                      inputProps={{ min: 1 }}
                      sx={inputSx}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Start Date (optional)"
                      name="startDate"
                      type="date"
                      value={formik.values.startDate}
                      onChange={formik.handleChange}
                      InputLabelProps={{ shrink: true }}
                      sx={inputSx}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="End Date (optional)"
                      name="endDate"
                      type="date"
                      value={formik.values.endDate}
                      onChange={formik.handleChange}
                      InputLabelProps={{ shrink: true }}
                      sx={inputSx}
                    />
                  </Grid>
                </Grid>
              </Card>
            </Box>
          </Grid>

          {/* Image Upload Sidebar */}
          <Grid item xs={12} md={4}>
            <Card title="Ad Image">
              <Box
                sx={{
                  border: isDark ? "2px dashed rgba(255,255,255,0.1)" : "2px dashed #e2e8f0",
                  borderRadius: "12px",
                  p: 3,
                  textAlign: "center",
                  backgroundColor: isDark ? "rgba(255,255,255,0.02)" : "#f8fafc",
                  transition: "all 0.2s",
                  "&:hover": {
                    borderColor: isDark ? "#6366f1" : "#1976d2",
                    backgroundColor: isDark ? "rgba(99,102,241,0.05)" : "rgba(25,118,210,0.04)",
                  },
                }}
              >
                {imageUrl ? (
                  <Box>
                    <Box
                      sx={{ position: "relative", display: "inline-block", cursor: "pointer" }}
                      onClick={() => setPreviewOpen(true)}
                    >
                      <img
                        src={imageUrl}
                        alt="Ad preview"
                        style={{ width: "100%", aspectRatio: "4 / 5", objectFit: "cover", borderRadius: 8 }}
                      />
                    </Box>
                    <Typography sx={{ mt: 1, fontSize: "0.75rem", color: isDark ? "#718096" : "#94a3b8" }}>
                      Click image to preview
                    </Typography>
                  </Box>
                ) : (
                  <Box>
                    <CloudUploadIcon sx={{ fontSize: 48, color: isDark ? "#475569" : "#cbd5e1", mb: 1 }} />
                    <Typography sx={{ fontSize: "0.875rem", color: isDark ? "#94a3b8" : "#64748b", mb: 0.5 }}>
                      Upload your ad image
                    </Typography>
                    <Typography sx={{ fontSize: "0.75rem", color: isDark ? "#64748b" : "#94a3b8" }}>
                      JPG or PNG · max 5 MB
                    </Typography>
                  </Box>
                )}

                <Button
                  variant="outlined"
                  component="label"
                  size="small"
                  disabled={uploading}
                  startIcon={uploading ? <CircularProgress size={14} /> : <CloudUploadIcon />}
                  sx={{
                    mt: 2,
                    textTransform: "none",
                    borderRadius: "8px",
                    borderColor: isDark ? "rgba(255,255,255,0.2)" : "#e2e8f0",
                    color: isDark ? "#ffffff" : "#1a202c",
                  }}
                >
                  {uploading ? "Uploading..." : imageUrl ? "Change Image" : "Choose Image"}
                  <input type="file" hidden accept="image/jpeg,image/png" onChange={handleFileChange} />
                </Button>
              </Box>
            </Card>

            {/* Placement Guide */}
            <Box sx={{ mt: 3 }}>
              <Card title="📍 Where Each Zone Appears">
                <Box sx={{ fontSize: "0.75rem", lineHeight: 1.8 }}>
                  {PLACEMENT_GUIDE.map((item) => (
                    <Box key={item.zone} sx={{ mb: 1.5, display: "flex", flexDirection: "column", gap: 0.25 }}>
                      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "baseline", gap: 1 }}>
                        <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color: isDark ? "#e2e8f0" : "#1a202c" }}>
                          {item.zone}:
                        </Typography>
                        <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: isDark ? "#a78bfa" : "#1976d2" }}>
                          {item.size}
                        </Typography>
                      </Box>
                      <Typography sx={{ fontSize: "0.7rem", color: isDark ? "#94a3b8" : "#64748b" }}>
                        {item.where}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Card>
            </Box>

            {/* Submit */}
            <Box sx={{ mt: 3 }}>
              <ActionButton
                variant="primary"
                fullWidth
                size="large"
                type="submit"
                loading={submitting}
                disabled={submitting || uploading}
              >
                {submitting ? "Saving..." : "Update Advertisement"}
              </ActionButton>
            </Box>
          </Grid>
        </Grid>
      </form>

      {/* Image Preview Dialog */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { backgroundColor: isDark ? "#1a1a1a" : "#fff", borderRadius: "16px" } }}
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: isDark ? "1px solid rgba(255,255,255,0.05)" : "1px solid #e2e8f0" }}>
          <Typography sx={{ fontWeight: 700, color: isDark ? "#fff" : "#1a202c" }}>Image Preview</Typography>
          <IconButton onClick={() => setPreviewOpen(false)} sx={{ color: isDark ? "#cbd5e1" : "#718096" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, textAlign: "center" }}>
          {imageUrl && <img src={imageUrl} alt="Ad" style={{ maxWidth: "100%", borderRadius: 8 }} />}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: isDark ? "1px solid rgba(255,255,255,0.05)" : "1px solid #e2e8f0" }}>
          <Button onClick={() => setPreviewOpen(false)} sx={{ textTransform: "none" }}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const mapStateToProps = (state) => ({ profile: state.user.profile });
export default connect(mapStateToProps)(Edit_Advertisement);
